import React, { useState, useEffect } from 'react';
import { Settings as SettingsIcon, Save, RefreshCw, ToggleLeft, ToggleRight, Eye, EyeOff, Shield } from 'lucide-react';
import { apiClient, RoleSettings } from '../services/apiClient';
import { useFeatureFlags } from '../hooks/useFeatureFlags';

const Settings: React.FC = () => {
  const [roleSettings, setRoleSettings] = useState<RoleSettings>({
    adminGroup: '',
    userGroup: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [saveMessage, setSaveMessage] = useState<string>('');
  const { features, isLoading: featuresLoading, updateFeatures } = useFeatureFlags();

  useEffect(() => {
    // Load current role settings from backend
    const loadRoleSettings = async () => {
      try {
        setIsLoading(true);
        const currentRoleSettings = await apiClient.getRoleSettings();
        setRoleSettings(currentRoleSettings);
      } catch (error) {
        console.error('Failed to load role settings:', error);
        setSaveMessage('Failed to load role settings from server');
        setTimeout(() => setSaveMessage(''), 5000);
      } finally {
        setIsLoading(false);
      }
    };

    loadRoleSettings();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage('');
    
    try {
      const updatedSettings = await apiClient.updateRoleSettings(roleSettings);
      setRoleSettings(updatedSettings);
      setSaveMessage('Role settings saved successfully! Changes will take effect immediately.');
      setTimeout(() => setSaveMessage(''), 5000);
    } catch (error) {
      setSaveMessage('Failed to save role settings. Please check your input and try again.');
      console.error('Error saving role settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    setIsSaving(true);
    setSaveMessage('');
    
    try {
      const defaultSettings: RoleSettings = {
        adminGroup: '',
        userGroup: ''
      };
      
      const resetSettings = await apiClient.updateRoleSettings(defaultSettings);
      setRoleSettings(resetSettings);
      setSaveMessage('Role settings reset to defaults');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      setSaveMessage('Failed to reset role settings');
      console.error('Error resetting role settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="bg-gray-800 rounded-lg shadow-sm border border-gray-700">
        <div className="px-6 py-4 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white flex items-center">
            <SettingsIcon className="w-5 h-5 mr-2" />
            Application Configuration
          </h2>
        </div>
        
        <div className="p-6 space-y-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <span className="ml-3 text-gray-300">Loading role settings...</span>
            </div>
          ) : (
            <>
              <div className="text-sm text-gray-400">
                Role management settings will appear here once loaded from the backend.
              </div>
            </>
          )}
        </div>
      </div>

      {/* Role Management Section */}
      <div className="bg-gray-800 rounded-lg shadow-sm border border-gray-700">
        <div className="px-6 py-4 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white flex items-center">
            <Shield className="w-5 h-5 mr-2" />
            Role Management
          </h2>
        </div>
        
        <div className="p-6 space-y-6">
          <div className="space-y-4">
            <h3 className="text-md font-medium text-white">User Role Configuration</h3>
            <p className="text-sm text-gray-400">
              Configure which Azure AD groups are assigned Administrator and User roles. The Administrator group gets full access to all clients, matters, and automatic SharePoint container permissions. The User group gets application access but limited data access.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="adminGroup" className="block text-sm font-medium text-gray-300 mb-2">
                  Administrator Group
                </label>
                <input
                  type="text"
                  id="adminGroup"
                  value={roleSettings.adminGroup}
                  onChange={(e) => setRoleSettings({ ...roleSettings, adminGroup: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Administrators@yourdomain.com"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Single Azure AD group UPN for administrators
                </p>
              </div>
              
              <div>
                <label htmlFor="userGroup" className="block text-sm font-medium text-gray-300 mb-2">
                  User Group
                </label>
                <input
                  type="text"
                  id="userGroup"
                  value={roleSettings.userGroup}
                  onChange={(e) => setRoleSettings({ ...roleSettings, userGroup: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Users@yourdomain.com"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Single Azure AD group UPN for regular users
                </p>
              </div>
            </div>

            <div className="bg-blue-900/20 border border-blue-700/30 rounded-md p-4">
              <h4 className="text-sm font-medium text-blue-300 mb-2">Role Hierarchy & Container Permissions</h4>
              <ul className="text-sm text-blue-200 space-y-1">
                <li>• <strong>Administrator:</strong> Full access to all clients, matters, and administrative features</li>
                <li>• <strong>Administrator:</strong> Automatically gets write permissions to all SharePoint containers</li>
                <li>• <strong>User:</strong> Application access with limited client/matter data (configurable per user)</li>
                <li>• If a user belongs to both groups, they receive Administrator privileges</li>
                <li>• Only one group per role is supported for simplified management</li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-700">
              <div className="flex space-x-3">
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  {isSaving ? 'Saving...' : 'Save Settings'}
                </button>
                
                <button
                  onClick={handleReset}
                  disabled={isSaving}
                  className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reset to Defaults
                </button>
              </div>
              
              {saveMessage && (
                <div className={`text-sm ${saveMessage.includes('success') ? 'text-green-400' : 'text-red-400'}`}>
                  {saveMessage}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* UI Feature Toggles Section */}
      <div className="bg-gray-800 rounded-lg shadow-sm border border-gray-700">
        <div className="px-6 py-4 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white flex items-center">
            {features?.dashboardShowReportCards ? <Eye className="w-5 h-5 mr-2" /> : <EyeOff className="w-5 h-5 mr-2" />}
            UI Feature Toggles
          </h2>
        </div>
        
        <div className="p-6 space-y-6">
          <p className="text-sm text-gray-400 mb-4">
            Control which features are visible in the application. These settings apply to all users.
          </p>
          
          {featuresLoading ? (
            <div className="text-gray-400">Loading feature flags...</div>
          ) : features ? (
            <div className="space-y-4">
              {/* Dashboard Features */}
              <div className="border-b border-gray-700 pb-4">
                <h3 className="text-md font-medium text-white mb-3">Dashboard Features</h3>
                
                <div className="flex items-center justify-between p-3 bg-gray-900 rounded-md">
                  <div>
                    <label className="text-sm font-medium text-gray-300">Dashboard Report Cards</label>
                    <p className="text-xs text-gray-500 mt-1">
                      Show/hide Total Clients, Active Matters, Revenue, and Productivity cards
                    </p>
                  </div>
                  <button
                    onClick={async () => {
                      await updateFeatures({ dashboardShowReportCards: !features.dashboardShowReportCards });
                    }}
                    className="flex items-center"
                  >
                    {features.dashboardShowReportCards ? (
                      <ToggleRight className="w-10 h-10 text-blue-500" />
                    ) : (
                      <ToggleLeft className="w-10 h-10 text-gray-500" />
                    )}
                  </button>
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-900 rounded-md">
                  <div>
                    <label className="text-sm font-medium text-gray-300">Dashboard Matters List</label>
                    <p className="text-xs text-gray-500 mt-1">
                      Show/hide the Recent Matters list on dashboard (to make space for medical records UI)
                    </p>
                  </div>
                  <button
                    onClick={async () => {
                      await updateFeatures({ dashboardShowMattersList: !features.dashboardShowMattersList });
                    }}
                    className="flex items-center"
                  >
                    {features.dashboardShowMattersList ? (
                      <ToggleRight className="w-10 h-10 text-blue-500" />
                    ) : (
                      <ToggleLeft className="w-10 h-10 text-gray-500" />
                    )}
                  </button>
                </div>
              </div>
              
              {/* Medical Records Features */}
              <div className="pb-4">
                <h3 className="text-md font-medium text-white mb-3">Medical Records Features</h3>
                
                <div className="flex items-center justify-between p-3 bg-gray-900 rounded-md">
                  <div>
                    <label className="text-sm font-medium text-gray-300">Medical Records Upload</label>
                    <p className="text-xs text-gray-500 mt-1">
                      Show medical records upload zone on dashboard
                    </p>
                  </div>
                  <button
                    onClick={async () => {
                      await updateFeatures({ medicalRecordsUploadEnabled: !features.medicalRecordsUploadEnabled });
                    }}
                    className="flex items-center"
                  >
                    {features.medicalRecordsUploadEnabled ? (
                      <ToggleRight className="w-10 h-10 text-blue-500" />
                    ) : (
                      <ToggleLeft className="w-10 h-10 text-gray-500" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-red-400">Failed to load feature flags</div>
          )}
        </div>
      </div>

      {/* Documentation Section */}
      <div className="bg-gray-800 rounded-lg shadow-sm border border-gray-700">
        <div className="px-6 py-4 border-b border-gray-700">
          <h3 className="text-md font-medium text-white">Container Permissions</h3>
        </div>
        
        <div className="p-6">
          <div className="prose prose-sm prose-invert max-w-none">
            <p className="text-gray-300 mb-4">
              When you create a new matter container, the configured Microsoft 365 Group will automatically be assigned 
              <strong> write permissions</strong> to the container. This allows all members of the group to:
            </p>
            <ul className="text-gray-300 space-y-1 mb-4">
              <li>• Upload, edit, and delete documents</li>
              <li>• Create folders and organize content</li>
              <li>• Share documents with external users</li>
              <li>• Manage document metadata and properties</li>
            </ul>
            <p className="text-sm text-gray-400">
              <strong>Note:</strong> Changes to these settings will only affect newly created containers. 
              Existing containers will retain their current permission settings.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
